import { ResizableHandle, ResizablePanel, ResizablePanelGroup, } from "../components/Resizable";
import { cn } from "../lib/utils";
import React from "react";
/**
 * Docks a resizable, collapsible panel to the right of the main content,
 * outside of whatever container the content itself uses. The divider is
 * draggable and the panel animates open and shut. The forwarded ref exposes
 * the panel's imperative handle for hosts that resize it programmatically.
 *
 * @example
 * ```tsx
 * <ResizableSidePanel isOpen={isOpen} panel={<MyPanel />}>
 *   <PageContent />
 * </ResizableSidePanel>
 * ```
 */
export const ResizableSidePanel = React.forwardRef(({ children, panel, isOpen = false, defaultSize = 30, minSize = 20, minContentSize = 30, isResizable = true, onCollapse, minContentWidthPx, onContentSqueezed, className, }, ref) => {
    const panelRef = React.useRef(null);
    const rowRef = React.useRef(null);
    const [panelContentSizePercent, setPanelContentSizePercent] = React.useState(defaultSize);
    React.useImperativeHandle(ref, () => panelRef.current);
    React.useEffect(() => {
        if (!panelRef.current) {
            return;
        }
        if (!isOpen) {
            panelRef.current.collapse();
        }
        else if (panelRef.current.isCollapsed()) {
            panelRef.current.expand(defaultSize);
        }
        else {
            panelRef.current.resize(defaultSize);
        }
    }, [isOpen, defaultSize]);
    const handlePanelResize = React.useCallback((panelSizePercent) => {
        if (panelSizePercent > 0) {
            setPanelContentSizePercent(panelSizePercent);
        }
        if (!onContentSqueezed || !minContentWidthPx || !rowRef.current) {
            return;
        }
        const rowWidthPx = rowRef.current.getBoundingClientRect().width;
        const contentWidthPx = (rowWidthPx * (100 - panelSizePercent)) / 100;
        if (contentWidthPx < minContentWidthPx) {
            onContentSqueezed();
        }
    }, [onContentSqueezed, minContentWidthPx]);
    return (React.createElement("div", { ref: rowRef, className: cn("relative flex h-full w-full flex-1 @container", className) },
        React.createElement(ResizablePanelGroup, { animateLayoutChanges: true, direction: "horizontal", className: "flex h-full w-full flex-1" },
            React.createElement(ResizablePanel, { defaultSize: 100, minSize: minContentSize }, children),
            React.createElement(ResizableHandle, { withHandle: isOpen && isResizable, disabled: !isOpen || !isResizable, className: "z-50 hidden md:flex", onDragging: (isDragging) => {
                    var _a;
                    // Pointer resizing skips transitions, so release completes a drag collapse.
                    if (!isDragging && ((_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.isCollapsed())) {
                        onCollapse === null || onCollapse === void 0 ? void 0 : onCollapse();
                    }
                } }),
            React.createElement(ResizablePanel, { ref: panelRef, defaultSize: isOpen ? defaultSize : 0, minSize: isOpen ? minSize : 0, collapsedSize: 0, collapsible: true, onResize: handlePanelResize, onTransitionEnd: (event) => {
                    var _a;
                    // Programmatic and keyboard collapses settle when the motion completes.
                    if (event.target === event.currentTarget &&
                        event.propertyName === "flex-grow" &&
                        ((_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.isCollapsed())) {
                        onCollapse === null || onCollapse === void 0 ? void 0 : onCollapse();
                    }
                }, className: cn("overflow-hidden", isOpen
                    ? "absolute inset-0 z-50 md:relative md:inset-auto md:z-auto"
                    : "hidden md:block") },
                React.createElement("div", { className: "h-full min-w-full overflow-hidden bg-panel-background @container md:min-w-0", 
                    // Keep content width and container queries stable during animation.
                    style: { width: `${panelContentSizePercent}cqw` } }, panel)))));
});
ResizableSidePanel.displayName = "ResizableSidePanel";
//# sourceMappingURL=ResizableSidePanel.js.map